import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import useTitanApp from "@/hooks/useTitanApp";
import {
  getDrugBySearch,
  setselectedDrugs,
  setDrugSummary,
} from "@/dispatchers/nmsDispatchers";
// ✅ Validation Schema
// Lengths match the API's Medications model (add-patient-medication).
export const MEDICATION_TEXT_MAX_LENGTH = 500;
export const MEDICATION_NOTES_MAX_LENGTH = 5000;
export const MEDICATION_QUANTITY_MAX_LENGTH = 9;

export const medicationSchema = z.object({
  item: z
    .string()
    .min(1, "Item is required")
    .max(MEDICATION_TEXT_MAX_LENGTH, `Item must be ${MEDICATION_TEXT_MAX_LENGTH} characters or fewer`),
  dose: z
    .string()
    .min(1, "Dose is required")
    .max(MEDICATION_TEXT_MAX_LENGTH, `Dose must be ${MEDICATION_TEXT_MAX_LENGTH} characters or fewer`),
  quantity: z
    .string()
    .regex(/^\d*$/, "Quantity must be a whole number")
    .max(MEDICATION_QUANTITY_MAX_LENGTH, "Quantity is too large")
    .optional(),
  therapyLevel: z.enum(["Acute", "Chronic", "Maintenance", "AsNeeded"]),
  notes: z
    .string()
    .max(MEDICATION_NOTES_MAX_LENGTH, `Additional notes must be ${MEDICATION_NOTES_MAX_LENGTH} characters or fewer`)
    .optional(),
});

// Quantity must be a positive whole number. Anything that isn't only digits
// ("1.5", "-3", "1e5", "abc") is rejected and the previous value is kept,
// rather than stripped into a different number.
export const toWholeNumberText = (value: string, previous = "") => {
  const text = String(value ?? "").trim();
  if (!/^\d*$/.test(text)) return previous ?? "";
  return text.replace(/^0+/, "").slice(0, MEDICATION_QUANTITY_MAX_LENGTH);
};

// ✅ Types
export type MedicationForm = z.infer<typeof medicationSchema>;

export interface MedicationEditMode {
  id: string;
  item: string;
  dose: string;
  times?: {
    morning?: string;
    midday?: string;
    evening?: string;
    night?: string;
  };
  quantity?: string;
  therapyLevel: "Acute" | "Chronic" | "Maintenance" | "AsNeeded";
  preparation?: string;
  notes?: string;
}

// ✅ Static Data (unchanged)
export const therapyLevelOptions = [
  { value: "Chronic", label: "Chronic" },
  { value: "Acute", label: "Acute" },
  { value: "Maintenance", label: "Maintenance" },
  { value: "AsNeeded", label: "As Needed" },
];

// ✅ Mapping Helpers
export const therapyLevelNumberToString: Record<
  number,
  "Chronic" | "Acute" | "Maintenance" | "AsNeeded"
> = {
  1: "Chronic",
  2: "Acute",
  3: "Maintenance",
  4: "AsNeeded",
};

export const therapyLevelStringToNumber: Record<
  "Chronic" | "Acute" | "Maintenance" | "AsNeeded",
  number
> = {
  Chronic: 1,
  Acute: 2,
  Maintenance: 3,
  AsNeeded: 4,
};

// ✅ Normalize backend data when it returns numbers
export const normalizeMedicationData = (data: any[]): MedicationEditMode[] => {
  return data.map((item) => ({
    ...item,
    therapyLevel:
      typeof item.therapyLevel === "number"
        ? therapyLevelNumberToString[item.therapyLevel] ?? "Acute"
        : (item.therapyLevel as
            | "Acute"
            | "Chronic"
            | "Maintenance"
            | "AsNeeded"),
  }));
};

// ✅ Custom Hook for Medication Form State
export const useMedicationFormState = (editMode?: MedicationEditMode) => {
  const [open, setOpen] = useState(false);

  const formMethods = useForm<MedicationForm>({
    resolver: zodResolver(medicationSchema),
    defaultValues: editMode
      ? {
          item: editMode.item,
          dose: editMode.dose,
          quantity: editMode.quantity || "",
          therapyLevel:
            typeof editMode.therapyLevel === "number"
              ? therapyLevelNumberToString[editMode.therapyLevel]
              : editMode.therapyLevel,
          notes: editMode.notes || "",
        }
      : {
          therapyLevel: "Acute",
        },
  });

  return {
    open,
    setOpen,
    ...formMethods,
  };
};

// ✅ Convert string → number on submit
export const handleSubmit = (
  data: MedicationForm,
  onSave: (medication: any) => void,
  setOpen: (open: boolean) => void,
  reset: () => void,
) => {
  const payload = {
    ...data,
    therapyLevel: therapyLevelStringToNumber[data.therapyLevel],
  };

  onSave(payload);
  setOpen(false);
  reset();
};

export const useMedicationDose = () => {
  const { pharmacyServiceState, nmsServiceState } = useTitanApp();
  const { doseShortCodeList } = pharmacyServiceState || {};
  const [searchLoading, setSearchLoading] = useState(false);
  const {} = useTitanApp();
  const { drugSummary } = nmsServiceState || {};

  const handleDose = (val) => {
    let valueToSet;

    const endsWithSpace = val?.endsWith(" ");

    if (endsWithSpace) {
      const words = val?.trim().split(" ");
      const lastWord = words[words.length - 1];
      const matched = doseShortCodeList?.find(
        (entry) => entry?.shortCode?.toLowerCase() === lastWord.toLowerCase(),
      );
      if (matched) {
        words[words.length - 1] = matched.description;
        valueToSet = words.join(" ") + " ";
      } else {
        valueToSet = val;
      }
    } else {
      valueToSet = val;
    }

    return valueToSet;
  };

  const drugSearch = async (keyword: string) => {
    if (!keyword || keyword.trim().length < 2) return;
    setSearchLoading(true);
    try {
      const response = await getDrugBySearch({ keyword: keyword });
      if (Array.isArray(response)) {
        setselectedDrugs(response);
        setDrugSummary(response);
      }
    } finally {
      setSearchLoading(false);
    }
  };

  return {
    handleDose,
    searchLoading,
    drugSearch,
    drugSummary,
  };
};
